// The account panel's pure parts (Phase 4, item 6): the rail's tabs, the fact strip above them,
// and the header's hide action. Kept apart from the components so `node --test` covers them.
import type { BookAccount, Opportunity } from '../api/types.ts';
import { lastOutbound } from '../book/derived.ts';
import { TIER_ORDER } from '../book/filters.ts';
import type { BoardPref } from '../book/boardPref.ts';
import { nextPreference } from '../book/boardPref.ts';
import { daysSince, formatCredits } from '../lib/format.ts';
import { q4Score, q4ScoreTitle } from '../lib/q4Target.ts';

export const PANEL_TABS = ['Scout', 'Company', 'Outreach', 'Research', 'History'] as const;
export type PanelTab = typeof PANEL_TABS[number];

export interface StripFact {
  label: string;
  value: string;
  // A short qualifier after the value: the usage change, the deal's stage.
  note: string | null;
  tone: 'up' | 'down' | null;
  // Draws the value itself in the warn colour (the AE strip's "1. Qualification" stage).
  valueTone?: 'warn' | null;
  // A hover explaining the value: the Q4 score's breakdown.
  title?: string | null;
}

/** Open deals: anything Salesforce has not marked closed. An unknown flag counts as open, so no
 * deal drops out of both lists. */
export function openOpportunities(row: BookAccount): Opportunity[] {
  return row.opportunities.filter(deal => deal.closed !== true);
}

/** The figure on one deal, the one rule every surface uses: Salesforce's typed Amount, else its
 * qualified pipeline, else null (drawn as "—", never as "$0" or "not quantified"). An Amount of 0
 * is Salesforce's unset default, so it counts as missing; so does a qualified pipeline of 0. */
export function dealAmount(deal: Opportunity): number | null {
  return deal.amount || deal.qualified_pipeline || null;
}

/** A deal stage's leading number: "2. Scoping" → 2. Null when the stage carries none. */
function stageNumber(stage: string | null | undefined): number | null {
  const match = stage?.match(/^\s*(\d+)/);
  return match ? Number(match[1]) : null;
}

/** "Opportunity - 2" from the furthest-along open deal's "2. Scoping", else the Salesforce
 * account status. With no numbered stage, the word Opportunity and the first stage as its note. */
function stageFact(row: BookAccount): StripFact {
  const open = openOpportunities(row);
  if (open.length) {
    const numbers = open.map(deal => stageNumber(deal.stage)).filter((value): value is number => value != null);
    if (numbers.length) {
      const furthest = Math.max(...numbers);
      return { label: 'Stage', value: `Opportunity - ${furthest}`, note: null, tone: null };
    }
    return { label: 'Stage', value: 'Opportunity', note: open[0]?.stage ?? null, tone: null };
  }
  return { label: 'Stage', value: row.status || '—', note: null, tone: null };
}

/** Thirty-day credits with the change against the thirty days before. */
export function usageFact(row: BookAccount): StripFact {
  if (row.credits_30 == null && row.credits_prior_30 == null) {
    return { label: 'Usage 30d', value: '—', note: null, tone: null };
  }
  const trend = row.credits_trend_pct;
  const note = trend == null ? null : `${trend > 0 ? '+' : ''}${Math.round(trend)}%`;
  const tone = trend == null || trend === 0 ? null : trend > 0 ? 'up' : 'down';
  return { label: 'Usage 30d', value: compactNumber(row.credits_30), note, tone };
}

/** The account's Cartesia plan: the highest across its product orgs and the account rollup, or
 * null with none. The fact strip and the Company tab both read this. */
export function highestPlan(row: BookAccount): string | null {
  const tiers = [row.tier, ...row.orgs.map(org => org.tier)]
    .map(tier => (tier || '').trim().toLowerCase())
    .filter(tier => TIER_ORDER.includes(tier));

  // TIER_ORDER is highest first, so the first plan it holds is the account's highest.
  return TIER_ORDER.find(tier => tiers.includes(tier)) ?? null;
}

/** The plan, or "—" with none. */
function planFact(row: BookAccount): StripFact {
  return { label: 'Plan', value: highestPlan(row) || '—', note: null, tone: null };
}

/** The account owner: the same `account_owner` the Company tab labels "AE owner". */
function aeOwnerFact(row: BookAccount): StripFact {
  return { label: 'AE owner', value: row.account_owner || '—', note: null, tone: null };
}

/** The last thing anyone sent: the same `lastOutbound` the header and the Company tab read. */
function lastOutFact(row: BookAccount, today: string): StripFact {
  const day = lastOutbound(row)?.day;
  if (!day) return { label: 'Last out', value: 'Never', note: null, tone: null };
  const days = daysSince(day, new Date(today + 'T00:00:00Z'));
  return { label: 'Last out', value: days <= 0 ? 'Today' : `${days}d ago`, note: null, tone: null };
}

/** "Q4 priority 71", with what the score is made of on hover; null off the Q4 lists. */
export function q4Fact(row: BookAccount): StripFact | null {
  const target = row.q4_target;
  const score = q4Score(target);
  if (!target || score == null) return null;
  return { label: 'Q4 priority', value: String(score), note: null, tone: null, title: q4ScoreTitle(target) };
}

/** The five facts the strip shows above every tab, in order, led by the Q4 priority when the
 * account is on a Q4 list. */
export function factStrip(row: BookAccount, today: string): StripFact[] {
  const facts = [stageFact(row), planFact(row), usageFact(row), aeOwnerFact(row), lastOutFact(row, today)];
  const q4 = q4Fact(row);
  return q4 ? [q4, ...facts] : facts;
}

/** 184320 → "184k", 1.2e6 → "1.2M"; small numbers as they are. */
export function compactNumber(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value >= 1e6) return `${(value / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (value >= 1e4) return `${Math.round(value / 1e3)}k`;
  return formatCredits(value);
}

export interface HideAction {
  label: string;
  // True while a write for this account is in flight: a repeat click would fire a repeat write.
  disabled: boolean;
  // The preference the click sets: hidden, or none when it is already hidden.
  next: BoardPref;
}

/** The header menu's hide item. A hidden account offers Unhide; anything else offers Hide. */
export function hideAction(current: BoardPref, pending = false): HideAction {
  const next = nextPreference(current, 'hidden');
  return { label: current === 'hidden' ? 'Unhide on my board' : 'Hide from my board', next, disabled: pending };
}

/** The link for an account: the page opens it by `?account=`. The board by default; the caller
 * passes the reader's own page (`accountLinkBase` in `lib/roleViews.ts`). */
export function accountLink(origin: string, accountId: string, basePath = '/execution'): string {
  return `${origin}${basePath}?account=${encodeURIComponent(accountId)}`;
}
